import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AUTH_STORAGE_KEYS,
  decodeJwtPayload,
  redirectToWorkspaceSubdomainWithHandoff,
  useAuth,
} from '@aimsos/auth';
import { useDebounce } from '@aimsos/hooks';
import {
  fetchTokenForWorkspace,
  getWorkspaceById,
  listMyWorkspaces,
  persistActiveWorkspaceId,
  readPersistedWorkspaceId,
} from '../services/workspaces.js';

/**
 * @typedef {Object} UseWorkspacesResult
 * @property {boolean} isLoading
 * @property {boolean} isLoadingMore
 * @property {string|null} error
 * @property {Array<{
 *   id: string, name: string, initials: string, role?: string,
 *   membershipRole?: string, avatarUrl?: string, email?: string
 * }>} tenants
 * @property {string} currentTenantId
 * @property {(id: string) => Promise<void>} switchTo
 * @property {boolean} isSwitching
 * @property {string|null} switchError
 * @property {() => void} clearSwitchError
 * @property {() => Promise<void>} refetch
 * @property {string} searchKeyword
 * @property {(value: string) => void} onSearchChange
 * @property {boolean} hasMore
 * @property {() => void} loadMore
 * @property {number} totalCount
 * @property {Array<object>} resultTenants
 */

/**
 * Map a canonical Workspace to the Topbar tenant shape the UI already
 * understands (`id`, `name`, `role`, `initials`, `email`, `avatarSrc`).
 *
 * `role` is intentionally overwritten with the localized subtitle label
 * (`Current` for the active tenant, `Switch` for the rest) so the dropdown
 * surfaces the action. That overwrite used to DISCARD the server's real
 * membership role, which the Workspace Selector needs (role badge, sort by
 * role, and which studio chips a user may see) — so it is carried through
 * untouched as `membershipRole` alongside it.
 *
 * @param {import('../services/workspaces.js').Workspace} ws
 * @param {string} currentTenantId
 * @param {{ current: string, switch: string }} labels
 */
function toTopbarTenant(ws, currentTenantId, labels) {
  if (!ws || !ws.id) return null;
  const isCurrent = ws.id === currentTenantId;
  return {
    id: ws.id,
    name: ws.name,
    // Carried through so WorkspaceGate can match "the workspace whose slug
    // is the one in the current URL" against this list — see
    // apps/core-shell/src/shell/WorkspaceGate.jsx.
    slug: ws.slug,
    role: isCurrent ? labels.current : labels.switch,
    // The server's own role, before the label overwrite above. Undefined until
    // `POST /v0/workspace/list` returns it; the selector falls back to its mock.
    membershipRole: ws.role,
    initials: ws.initials || 'WS',
    email: ws.email,
    avatarSrc: ws.avatarUrl,
  };
}

/**
 * Hook that exposes the workspaces list + the current tenant + a `switchTo`
 * action. Supports:
 *
 * - Backend search with 500ms debounce (resets pagination on each new query)
 * - Infinite-scroll pagination — call `loadMore` to append the next page
 * - No client-side status filter — the API already scopes results to accessible workspaces
 *
 * `switchTo` calls `POST /v1/token` with `{ workspace_uuid, user_uuid }`,
 * applies the returned token via `tokenService.setToken`, persists the new
 * workspace_uuid to localStorage, then navigates to the base module route
 * (first path segment) so every micro-frontend re-initialises with the new
 * tenant context without landing on a deep dynamic URL that may not exist in
 * the new workspace.
 *
 * @param {{ fallbackName?: string, labels?: { current: string, switch: string } }} [options]
 * @returns {UseWorkspacesResult}
 */
export function useWorkspaces({
  fallbackName = 'Workspace',
  labels = { current: 'Current', switch: 'Switch' },
} = {}) {
  // Current display profile (name/email/picture) — resolves correctly
  // whether it comes from Auth0's own `user` (this origin did the identity
  // login) or the cached profile from a prior handoff (see profileCache.js),
  // so it can be carried along in the NEXT handoff if this switch crosses
  // to a different {slug}.{rootDomain} subdomain.
  const { user: authUser, token: authToken } = useAuth();

  // Lazy initializer runs once — stable across renders.
  const [fallbackId] = useState(() => readPersistedWorkspaceId() || 'workspace-main');

  const [rawItems, setRawItems] = useState([]);
  const [currentTenantId, setCurrentTenantId] = useState(fallbackId);

  // The active JWT's own tenant_uuid claim is the real source of truth for
  // "which workspace is current" — it's always correct immediately, even
  // right after a switch, and works in both real and dummy-workspace mode.
  // The `workspace_uuid` localStorage key (read into `fallbackId` above)
  // is NOT reliable for this: it's deliberately left untouched during a
  // dummy-mode switch (see switchTo below — writing a fake id there would
  // poison the real /v1/token exchange on the next login), so relying on it
  // alone left the Topbar chip stuck showing the previous workspace after
  // switching.
  useEffect(() => {
    const tenantFromToken = decodeJwtPayload(authToken)?.tenant_uuid;
    if (tenantFromToken) setCurrentTenantId(tenantFromToken);
  }, [authToken]);
  // currentWorkspace holds the full detail of the active workspace, fetched
  // directly by UUID on init so the chip always shows the correct name even
  // when the user's workspace is not on the first list page.
  const [currentWorkspace, setCurrentWorkspace] = useState(/** @type {import('../services/workspaces.js').Workspace|null} */ (null));
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const [hasMore, setHasMore] = useState(false);
  // Server-reported total across ALL pages. `tenants.length` only ever counts
  // the pages fetched so far, so anything that reports "you have access to N
  // workspaces" must use this instead (see WorkspacePicker's access count).
  const [totalCount, setTotalCount] = useState(0);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchError, setSwitchError] = useState(/** @type {string|null} */ (null));

  // Tracks whether the initial data has ever arrived. After the first
  // successful fetch, subsequent non-append fetches (search, manual refetch)
  // use isRefetching instead of isLoading so the chip never shows a skeleton.
  const hasLoadedOnce = useRef(false);

  // Controlled search input value (exposed to TopbarTenantsSection).
  const [searchKeyword, setSearchKeyword] = useState('');
  const debouncedSearch = useDebounce(searchKeyword, 500);

  // Stable refs — avoids adding them to useCallback dependency arrays.
  const abortControllerRef = useRef(/** @type {AbortController|null} */ (null));
  const fetchIdRef = useRef(0);
  const currentPageRef = useRef(1);
  // Keeps refetch stable: reading through a ref lets the callback always use
  // the latest debounced value without listing it as a dependency.
  const debouncedSearchRef = useRef(debouncedSearch);
  debouncedSearchRef.current = debouncedSearch;

  /**
   * Core fetch. `append=true` adds the next page to the existing list;
   * `append=false` (default) replaces it entirely (new search or refetch).
   *
   * IMPORTANT: `currentTenantId` is NEVER modified by a search or append
   * fetch. It is the user's active workspace from auth (JWT → localStorage),
   * independent of what the search results or the current list page contains.
   * Only on the very first load — when there is truly no workspace UUID in
   * localStorage — do we fall back to the first ready item in the list.
   */
  const doFetch = useCallback(
    async ({ page, search, append = false }) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const requestId = ++fetchIdRef.current;
      // Decide which loading flag to raise so the UI knows what changed.
      // - Initial load (no data yet): isLoading → shows full skeleton.
      // - Search / refetch after first data: isRefetching → list-only skeleton.
      // - Append (infinite scroll): isLoadingMore → sentinel skeleton.
      const isFirstLoad = !hasLoadedOnce.current;
      if (append) {
        setIsLoadingMore(true);
      } else if (isFirstLoad) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }
      setError(null);

      try {
        // Trimmed: the raw input value would send " gary" and match nothing,
        // while the UI already treats a whitespace-only query as "no query".
        const keyword = search?.trim();
        const { items, meta, total } = await listMyWorkspaces(
          { page, page_size: 15, search: keyword || undefined },
          controller.signal,
        );

        if (requestId !== fetchIdRef.current) return;

        // The API already scopes the response to workspaces the authenticated
        // user can access. Unlike ann-web-app (where status === 'ready' meant
        // "operational"), this platform uses 'in_progress' for workspaces that
        // are provisioning but still fully switchable. No client-side status
        // filter is applied — show everything the API returns.
        setRawItems((prev) => (append ? [...prev, ...items] : items));

        // Only on the very first load (no search query, first page, not
        // appending): if there is no real workspace UUID from auth yet
        // (the generic 'workspace-main' placeholder), fall back to the
        // first item so the chip is never empty.
        // Search and loadMore fetches MUST NOT touch currentTenantId.
        if (!search && !append && page === 1) {
          setCurrentTenantId((current) => {
            if (current && current !== 'workspace-main') return current;
            return items[0]?.id || current;
          });
        }

        hasLoadedOnce.current = true;
        setHasMore(Boolean(meta?.has_next));
        // `total` falls back to items.length in the service when the server
        // sends no meta, so on an append that would shrink the count — take
        // the larger of the two.
        setTotalCount((prev) => (append ? Math.max(prev, total ?? 0) : (total ?? 0)));
      } catch (e) {
        if (e.name === 'AbortError' || e.name === 'CanceledError') return;
        if (requestId !== fetchIdRef.current) return;
        setError(e?.message || 'Failed to load workspaces');
      } finally {
        if (requestId === fetchIdRef.current) {
          if (append) setIsLoadingMore(false);
          else if (isFirstLoad) setIsLoading(false);
          else setIsRefetching(false);
        }
      }
    },
    [],
  );

  // Stable refetch — reads the current debounced search through a ref so its
  // identity never changes on search input, preventing consumers from
  // accidentally triggering extra fetches when they add refetch to dep arrays.
  const refetch = useCallback(() => {
    currentPageRef.current = 1;
    return doFetch({ page: 1, search: debouncedSearchRef.current, append: false });
  }, [doFetch]);

  // Run on mount — fetch the list and the current workspace detail in parallel.
  // doFetch is useCallback(fn, []) so it is stable; listing it here is correct
  // and removes the need for an eslint-disable suppression.
  useEffect(() => {
    currentPageRef.current = 1;
    doFetch({ page: 1, search: '', append: false });

    // Fetch the active workspace by UUID so the chip always shows the correct
    // name regardless of which list page the workspace appears on.
    let cancelled = false;
    const authWorkspaceId = readPersistedWorkspaceId();
    if (authWorkspaceId && authWorkspaceId !== 'workspace-main') {
      getWorkspaceById(authWorkspaceId)
        .then((ws) => { if (!cancelled) setCurrentWorkspace(ws); })
        .catch(() => {
          // Non-fatal — chip will fall back to list data or placeholder.
        });
    }

    return () => {
      cancelled = true;
      abortControllerRef.current?.abort();
    };
  }, [doFetch]);

  // Re-fetch when the debounced search changes (after initial mount).
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    currentPageRef.current = 1;
    doFetch({ page: 1, search: debouncedSearch, append: false });
  }, [debouncedSearch, doFetch]);

  /**
   * Append the next page of results. No-op when already loading or no more pages.
   */
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    const nextPage = currentPageRef.current + 1;
    currentPageRef.current = nextPage;
    doFetch({ page: nextPage, search: debouncedSearch, append: true });
  }, [isLoadingMore, hasMore, debouncedSearch, doFetch]);

  /**
   * Handle search input changes from the Topbar dropdown. The debounce in
   * this hook takes care of not firing a request on every keystroke.
   */
  const onSearchChange = useCallback((value) => {
    setSearchKeyword(typeof value === 'string' ? value : '');
  }, []);

  /**
   * Switch the active workspace.
   *
   * Calls `POST /v0/token` with `{ workspace_uuid, user_uuid }` to obtain a
   * new JWT scoped to the selected workspace (mirrors the ann-web-app pattern).
   * The token is applied via `tokenService.setToken` inside the service call,
   * then `localStorage.workspace_uuid` is updated so every subsequent token
   * refresh (proactive/reactive) and every reload targets the new workspace.
   * Finally the browser navigates to the new workspace's {slug}.{rootDomain}
   * subdomain (falling back to the base module route on the same origin when
   * the JWT has no workspace_slug claim) so every micro-frontend
   * re-initialises with the correct tenant context.
   *
   * The workspace persists across page refreshes and only resets on sign-out
   * (`clearAuthAndRedirect` wipes `localStorage.workspace_uuid`) or on another
   * explicit workspace switch.
   *
   * `isSwitching` is `true` while the request is in-flight. On failure,
   * `switchError` is populated and `isSwitching` resets so the user can retry.
   */
  const clearSwitchError = useCallback(() => setSwitchError(null), []);

  const switchTo = useCallback(async (workspaceId, options = {}) => {
    if (!workspaceId || isSwitching || workspaceId === currentTenantId) return;
    setIsSwitching(true);
    setSwitchError(null);
    try {
      const userUuid = localStorage.getItem(AUTH_STORAGE_KEYS.USER_UUID) || '';
      // POST /v0/token with { workspace_uuid, user_uuid } — returns a JWT
      // scoped to the selected workspace; tokenService.setToken is called
      // internally so the Axios client picks it up immediately.
      const token = await fetchTokenForWorkspace(workspaceId, userUuid);
      // Persist workspace_uuid so proactive/reactive refreshes on the next
      // page load continue using the selected workspace.
      persistActiveWorkspaceId(workspaceId);
      // Navigate to the base module route instead of reloading in-place.
      // Reloading on a deep dynamic path (e.g. /governance/sandbox/:id) would
      // send the user to a 404 because that entity may not exist in the new
      // workspace. Stripping to the first path segment (the MFE root) matches
      // ann-web-app's getModuleInitialRoute pattern. Also excludes
      // "/select-workspace" itself — switching FROM the picker must land on
      // the new workspace's dashboard, not carry the picker route along.
      // `options.path` overrides this — used when WorkspaceGate is honoring
      // a path the user typed directly (e.g. a deep link on {slug}.
      // {rootDomain} that bounced through the base URL for identity login;
      // see pendingWorkspaceRedirect.js), since the CURRENT pathname at that
      // point is on the base origin and has nothing to do with where they
      // meant to land.
      const [, firstSegment] = globalThis.location.pathname.split('/');
      const fallbackPath =
        options.path ||
        (firstSegment && firstSegment !== 'select-workspace' ? `/${firstSegment}` : '/');
      // If the target workspace lives on a different subdomain, hand OUR OWN
      // JWT directly to that origin via a one-time URL fragment (see
      // tokenHandoff.js) instead of bouncing through Auth0 again — Auth0 is
      // only ever used once, for identity, not per workspace switch. The
      // cached display profile (name/email/picture) travels along too, so
      // the destination origin can show it even though Auth0 SDK has no
      // session there.
      const workspaceSlug = decodeJwtPayload(token)?.workspace_slug;
      const redirected = redirectToWorkspaceSubdomainWithHandoff(
        workspaceSlug,
        {
          token,
          profile: authUser
            ? { name: authUser.name, email: authUser.email, picture: authUser.picture }
            : undefined,
        },
        // Without this, the handoff defaults to the CURRENT pathname (e.g.
        // "/select-workspace" when switching from the picker, or a deep
        // module route that may not exist in the new workspace) instead of
        // the base module route computed above.
        { path: fallbackPath },
      );
      if (!redirected) {
        globalThis.location.href = fallbackPath;
      }
    } catch (e) {
      setSwitchError(e?.message || '');
      setIsSwitching(false);
    }
  }, [isSwitching, currentTenantId, authUser]);

  return useMemo(() => {
    // Build the dropdown list from the paginated results.
    const listTenants = rawItems
      .map((item) => toTopbarTenant(item, currentTenantId, labels))
      .filter(Boolean);

    // If the active workspace was fetched by UUID but isn't present in the
    // current page of results (e.g. it lives on page 2+), inject it at the
    // top so the chip always resolves to the correct workspace.
    let tenants = listTenants;
    if (currentWorkspace && !rawItems.some((item) => item.id === currentWorkspace.id)) {
      const currentEntry = toTopbarTenant(currentWorkspace, currentTenantId, labels);
      if (currentEntry) tenants = [currentEntry, ...listTenants];
    }

    // While loading or when the list is empty (e.g. search returned nothing),
    // fall back first to the currentWorkspace detail, then to a bare placeholder.
    let resolvedTenants = tenants;
    if (tenants.length === 0) {
      const placeholderName = currentWorkspace?.name || fallbackName;
      resolvedTenants = [
        { id: currentTenantId, name: placeholderName, role: labels.current, initials: 'WS' },
      ];
    }

    return {
      // Exactly what the server returned for the CURRENT query, with neither
      // the pinned active workspace nor the placeholder below. `tenants` keeps
      // both because the Topbar chip must always resolve to something; a
      // search RESULT list must not show a workspace the query didn't match,
      // nor a fabricated row when nothing matched. See WorkspacePicker.
      resultTenants: listTenants,
      isLoading,
      isRefetching,
      isLoadingMore,
      error,
      tenants: resolvedTenants,
      currentTenantId,
      switchTo,
      isSwitching,
      switchError,
      clearSwitchError,
      refetch,
      searchKeyword,
      onSearchChange,
      hasMore,
      loadMore,
      totalCount: totalCount || resolvedTenants.length,
    };
  }, [
    isLoading,
    isRefetching,
    isLoadingMore,
    error,
    rawItems,
    currentTenantId,
    currentWorkspace,
    switchTo,
    isSwitching,
    switchError,
    clearSwitchError,
    refetch,
    searchKeyword,
    onSearchChange,
    hasMore,
    loadMore,
    totalCount,
    fallbackName,
    labels,
  ]);
}

/**
 * Pure helper exported for unit tests — turns a raw server response item
 * into the Topbar tenant shape.
 */
export { toTopbarTenant };
